/**
 * @module services/indexedDBService
 * @description IndexedDB persistence service
 *
 * Provides robust client-side storage using IndexedDB with:
 * - Settings storage (selected library/collection, preferences)
 * - Cache storage with TTL support
 * - Search history tracking
 * - Migration support for schema updates
 * - Fallback to localStorage when IndexedDB unavailable
 *
 * Note: API key storage has been removed - use Supabase proxy mode
 *
 * @example
 * import { indexedDBService } from '@services/indexedDBService';
 *
 * // Initialize (call once at app startup)
 * await indexedDBService.init();
 *
 * // Use the service
 * indexedDBService.setSelectedLibrary({ id: 123, type: 'user' });
 */

import { storage, ui, items, i18n } from '@/config';
import { createLogger } from './loggerService';
import type {
  StoreName,
  SettingsRecord,
  CacheRecord,
  SearchHistoryRecord,
  StoredLibrary,
  StoredPreferences,
  IIndexedDBService,
  Migration,
  UserPreferences,
} from '@types';
import { DB_VERSION, STORAGE_KEYS, DEFAULT_CACHE_CONFIG, DEFAULT_USER_PREFERENCES } from '@types';
import type { UIPreferences } from '@types';

const log = createLogger('storage');

// =============================================================================
// CONSTANTS
// =============================================================================

/** Database name from config */
const DB_NAME = storage.dbName;

/** Store names */
const STORES: Record<string, StoreName> = {
  SETTINGS: 'settings',
  CACHE: 'cache',
  SEARCH_HISTORY: 'searchHistory',
} as const;

/** Settings keys */
const SETTINGS_KEYS = {
  SELECTED_LIBRARY: 'selectedLibrary',
  SELECTED_COLLECTION: 'selectedCollection',
  UI_PREFERENCES: 'uiPreferences',
  USER_PREFERENCES: 'userPreferences',
  LOCALE: 'locale',
} as const;

/** Default UI preferences - values from config */
const DEFAULT_UI_PREFERENCES: UIPreferences = {
  collectionsWidth: ui.defaultCollectionsWidth,
  detailsWidth: ui.defaultDetailsWidth,
  sortColumn: items.defaultSortColumn,
  sortDirection: items.defaultSortDirection,
  locale: i18n.defaultLocale,
};

// =============================================================================
// MIGRATIONS
// =============================================================================

const MIGRATIONS: Migration[] = [
  {
    version: 1,
    description: 'Initial schema - settings, cache, searchHistory stores',
    migrate: (db: IDBDatabase) => {
      // Settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }

      // Cache store
      if (!db.objectStoreNames.contains('cache')) {
        const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
        cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      }

      // Search history store
      if (!db.objectStoreNames.contains('searchHistory')) {
        const historyStore = db.createObjectStore('searchHistory', {
          keyPath: 'query',
        });
        historyStore.createIndex('searchedAt', 'searchedAt', { unique: false });
      }
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if IndexedDB is available
 */
function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Wrap IDB request in a Promise
 */
function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Wrap IDB transaction in a Promise
 */
function promisifyTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(new Error('Transaction aborted'));
  });
}

// =============================================================================
// LOCALSTORAGE FALLBACK
// =============================================================================

/**
 * LocalStorage fallback service (used when IndexedDB unavailable)
 */
class LocalStorageFallback {
  getItem<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }

  setItem(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      log.error('LocalStorage write failed', { key, error: String(e) });
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }

  clear(): void {
    try {
      // Only clear refero-prefixed keys
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('refero-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Ignore
    }
  }
}

// =============================================================================
// INDEXEDDB SERVICE IMPLEMENTATION
// =============================================================================

/**
 * IndexedDB Service implementation
 */
class IndexedDBService implements IIndexedDBService {
  private db: IDBDatabase | null = null;
  private ready = false;
  private initPromise: Promise<void> | null = null;
  private fallback = new LocalStorageFallback();
  private useLocalStorage = false;

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Initialize the database
   */
  async init(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    if (!isIndexedDBAvailable()) {
      log.warn('IndexedDB not available, using localStorage fallback');
      this.useLocalStorage = true;
      this.ready = true;
      return;
    }

    try {
      this.db = await this.openDatabase();
      this.ready = true;
      log.info('IndexedDB initialized', { version: DB_VERSION });

      // Clean expired cache entries
      await this.cleanExpiredCache();
    } catch (error) {
      log.error('IndexedDB initialization failed, using localStorage fallback', {
        error: String(error),
      });
      this.useLocalStorage = true;
      this.ready = true;
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = request.result;
        const oldVersion = event.oldVersion;
        const transaction = request.transaction!;

        log.info('Database upgrade needed', {
          from: oldVersion,
          to: DB_VERSION,
        });

        // Run migrations
        for (const migration of MIGRATIONS) {
          if (migration.version > oldVersion) {
            log.debug('Running migration', {
              version: migration.version,
              description: migration.description,
            });
            migration.migrate(db, transaction);
          }
        }
      };

      request.onblocked = () => {
        log.warn('Database upgrade blocked - close other tabs');
        reject(new Error('Database upgrade blocked'));
      };
    });
  }

  /**
   * Check if database is ready
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Get database instance
   */
  getDB(): IDBDatabase | null {
    return this.db;
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.ready = false;
      this.initPromise = null;
      log.debug('Database connection closed');
    }
  }

  // ===========================================================================
  // INTERNAL HELPERS
  // ===========================================================================

  private async getSetting<T>(key: string): Promise<T | null> {
    if (this.useLocalStorage) {
      return this.fallback.getItem<T>(`refero-${key}`);
    }

    if (!this.db) return null;

    try {
      const transaction = this.db.transaction(STORES.SETTINGS, 'readonly');
      const store = transaction.objectStore(STORES.SETTINGS);
      const result = await promisifyRequest(store.get(key));
      return result ? ((result as SettingsRecord).value as T) : null;
    } catch (error) {
      log.error('Failed to get setting', { key, error: String(error) });
      return null;
    }
  }

  private async setSetting(key: string, value: unknown): Promise<void> {
    if (this.useLocalStorage) {
      this.fallback.setItem(`refero-${key}`, value);
      return;
    }

    if (!this.db) return;

    try {
      const transaction = this.db.transaction(STORES.SETTINGS, 'readwrite');
      const store = transaction.objectStore(STORES.SETTINGS);
      const record: SettingsRecord = {
        key,
        value,
        updatedAt: new Date().toISOString(),
      };
      await promisifyRequest(store.put(record));
    } catch (error) {
      log.error('Failed to set setting', { key, error: String(error) });
    }
  }

  private async deleteSetting(key: string): Promise<void> {
    if (this.useLocalStorage) {
      this.fallback.removeItem(`refero-${key}`);
      return;
    }

    if (!this.db) return;

    try {
      const transaction = this.db.transaction(STORES.SETTINGS, 'readwrite');
      const store = transaction.objectStore(STORES.SETTINGS);
      await promisifyRequest(store.delete(key));
    } catch (error) {
      log.error('Failed to delete setting', { key, error: String(error) });
    }
  }

  // ===========================================================================
  // LIBRARY
  // ===========================================================================

  getSelectedLibrary(): StoredLibrary | null {
    if (this.useLocalStorage) {
      const id = this.fallback.getItem<number>(STORAGE_KEYS.SELECTED_LIBRARY_ID);
      const type = this.fallback.getItem<string>(STORAGE_KEYS.SELECTED_LIBRARY_TYPE);
      const name = this.fallback.getItem<string>(STORAGE_KEYS.SELECTED_LIBRARY_NAME);

      if (id !== null && type) {
        return { id, type: type as 'user' | 'group', name: name || undefined };
      }
      return null;
    }

    // Sync fallback
    const id = this.fallback.getItem<number>(STORAGE_KEYS.SELECTED_LIBRARY_ID);
    const type = this.fallback.getItem<string>(STORAGE_KEYS.SELECTED_LIBRARY_TYPE);
    const name = this.fallback.getItem<string>(STORAGE_KEYS.SELECTED_LIBRARY_NAME);

    if (id !== null && type) {
      return { id, type: type as 'user' | 'group', name: name || undefined };
    }
    return null;
  }

  setSelectedLibrary(library: StoredLibrary): void {
    // Store in localStorage for sync access
    this.fallback.setItem(STORAGE_KEYS.SELECTED_LIBRARY_ID, library.id);
    this.fallback.setItem(STORAGE_KEYS.SELECTED_LIBRARY_TYPE, library.type);
    if (library.name) {
      this.fallback.setItem(STORAGE_KEYS.SELECTED_LIBRARY_NAME, library.name);
    }

    // Also store in IndexedDB
    this.setSetting(SETTINGS_KEYS.SELECTED_LIBRARY, library);
  }

  // ===========================================================================
  // COLLECTION
  // ===========================================================================

  getSelectedCollection(): string | null {
    return this.fallback.getItem<string>(STORAGE_KEYS.SELECTED_COLLECTION);
  }

  setSelectedCollection(collectionKey: string): void {
    this.fallback.setItem(STORAGE_KEYS.SELECTED_COLLECTION, collectionKey);
    this.setSetting(SETTINGS_KEYS.SELECTED_COLLECTION, collectionKey);
  }

  // ===========================================================================
  // UI PREFERENCES
  // ===========================================================================

  getUIPreferences(): UIPreferences {
    const stored = this.fallback.getItem<UIPreferences>(STORAGE_KEYS.UI_PREFERENCES);
    return { ...DEFAULT_UI_PREFERENCES, ...stored };
  }

  setUIPreferences(prefs: Partial<UIPreferences>): void {
    const current = this.getUIPreferences();
    const updated = { ...current, ...prefs };
    this.fallback.setItem(STORAGE_KEYS.UI_PREFERENCES, updated);
    this.setSetting(SETTINGS_KEYS.UI_PREFERENCES, updated);
  }

  // ===========================================================================
  // LOCALE
  // ===========================================================================

  getLocale(): 'en' | 'fr' {
    const locale = this.fallback.getItem<string>(STORAGE_KEYS.LOCALE);
    return locale === 'fr' ? 'fr' : 'en';
  }

  setLocale(locale: 'en' | 'fr'): void {
    this.fallback.setItem(STORAGE_KEYS.LOCALE, locale);
    this.setSetting(SETTINGS_KEYS.LOCALE, locale);
  }

  // ===========================================================================
  // USER PREFERENCES (Item Types & Fields)
  // ===========================================================================

  getUserPreferences(): UserPreferences {
    const stored = this.fallback.getItem<UserPreferences>(STORAGE_KEYS.USER_PREFERENCES);
    if (!stored) {
      return { ...DEFAULT_USER_PREFERENCES };
    }
    // Merge with defaults to ensure all required fields exist
    return {
      ...DEFAULT_USER_PREFERENCES,
      ...stored,
    };
  }

  setUserPreferences(prefs: Partial<UserPreferences>): void {
    const current = this.getUserPreferences();
    const updated: UserPreferences = {
      ...current,
      ...prefs,
      updatedAt: new Date().toISOString(),
    };
    this.fallback.setItem(STORAGE_KEYS.USER_PREFERENCES, updated);
    this.setSetting(SETTINGS_KEYS.USER_PREFERENCES, updated);
  }

  // ===========================================================================
  // SEARCH HISTORY
  // ===========================================================================

  getSearchHistory(): string[] {
    if (this.useLocalStorage) {
      return this.fallback.getItem<string[]>(STORAGE_KEYS.SEARCH_HISTORY) || [];
    }

    // Sync fallback
    return this.fallback.getItem<string[]>(STORAGE_KEYS.SEARCH_HISTORY) || [];
  }

  addToSearchHistory(query: string): void {
    const trimmed = query.trim();
    if (!trimmed) return;

    // Update localStorage (sync)
    let history = this.getSearchHistory();
    history = history.filter((q) => q !== trimmed);
    history.unshift(trimmed);
    history = history.slice(0, ui.maxSearchHistory);
    this.fallback.setItem(STORAGE_KEYS.SEARCH_HISTORY, history);

    // Update IndexedDB (async)
    if (!this.useLocalStorage && this.db) {
      const record: SearchHistoryRecord = {
        query: trimmed,
        searchedAt: new Date().toISOString(),
      };

      const transaction = this.db.transaction(STORES.SEARCH_HISTORY, 'readwrite');
      const store = transaction.objectStore(STORES.SEARCH_HISTORY);
      store.put(record);
    }
  }

  clearSearchHistory(): void {
    this.fallback.setItem(STORAGE_KEYS.SEARCH_HISTORY, []);

    if (!this.useLocalStorage && this.db) {
      const transaction = this.db.transaction(STORES.SEARCH_HISTORY, 'readwrite');
      const store = transaction.objectStore(STORES.SEARCH_HISTORY);
      store.clear();
    }
  }

  // ===========================================================================
  // CACHE
  // ===========================================================================

  getCached<T>(key: string): T | null {
    if (this.useLocalStorage) {
      const cached = this.fallback.getItem<CacheRecord>(`refero-cache-${key}`);
      if (cached && new Date(cached.expiresAt) > new Date()) {
        return cached.data as T;
      }
      return null;
    }

    // Sync cache read not possible with IndexedDB
    // Use getCachedAsync for async access
    const cached = this.fallback.getItem<CacheRecord>(`refero-cache-${key}`);
    if (cached && new Date(cached.expiresAt) > new Date()) {
      return cached.data as T;
    }
    return null;
  }

  setCached<T>(key: string, data: T, ttlMs?: number): void {
    const ttl = ttlMs ?? DEFAULT_CACHE_CONFIG.defaultTTL;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + ttl);

    const record: CacheRecord = {
      key,
      data,
      cachedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    // Store in localStorage for sync access
    this.fallback.setItem(`refero-cache-${key}`, record);

    // Store in IndexedDB
    if (!this.useLocalStorage && this.db) {
      const transaction = this.db.transaction(STORES.CACHE, 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      store.put(record);
    }
  }

  clearCache(): void {
    // Clear localStorage cache
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('refero-cache-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear IndexedDB cache
    if (!this.useLocalStorage && this.db) {
      const transaction = this.db.transaction(STORES.CACHE, 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      store.clear();
    }

    log.debug('Cache cleared');
  }

  private async cleanExpiredCache(): Promise<void> {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction(STORES.CACHE, 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      const index = store.index('expiresAt');
      const now = new Date().toISOString();

      const range = IDBKeyRange.upperBound(now);
      const request = index.openCursor(range);

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      await promisifyTransaction(transaction);
      log.debug('Expired cache entries cleaned');
    } catch (error) {
      log.error('Failed to clean expired cache', { error: String(error) });
    }
  }

  // ===========================================================================
  // FULL CLEAR
  // ===========================================================================

  clear(): void {
    // Clear localStorage
    this.fallback.clear();

    // Clear all IndexedDB stores
    if (!this.useLocalStorage && this.db) {
      const stores: StoreName[] = ['settings', 'cache', 'searchHistory'];

      for (const storeName of stores) {
        try {
          const transaction = this.db.transaction(storeName, 'readwrite');
          const store = transaction.objectStore(storeName);
          store.clear();
        } catch (error) {
          log.error('Failed to clear store', { store: storeName, error: String(error) });
        }
      }
    }

    log.info('All storage cleared');
  }

  // ===========================================================================
  // EXPORT / IMPORT
  // ===========================================================================

  async exportData(): Promise<StoredPreferences> {
    const data: StoredPreferences = {
      selectedLibrary: this.getSelectedLibrary() || undefined,
      selectedCollection: this.getSelectedCollection() || undefined,
      ui: this.getUIPreferences(),
      userPreferences: this.getUserPreferences(),
      locale: this.getLocale(),
    };

    return data;
  }

  async importData(data: StoredPreferences): Promise<void> {
    if (data.selectedLibrary) {
      this.setSelectedLibrary(data.selectedLibrary);
    }

    if (data.selectedCollection) {
      this.setSelectedCollection(data.selectedCollection);
    }

    if (data.ui) {
      this.setUIPreferences(data.ui);
    }

    if (data.userPreferences) {
      this.setUserPreferences(data.userPreferences);
    }

    if (data.locale) {
      this.setLocale(data.locale);
    }

    log.info('Data imported successfully');
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

/** Singleton instance */
export const indexedDBService = new IndexedDBService();

/** Export class for testing */
export { IndexedDBService };

export default indexedDBService;
